import React, { useState } from "react";
import api from "../../api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "../../hooks/use-toast";
import { Mail, KeyRound } from "lucide-react";

function Field({ label, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#16243b] mb-1.5">{label}</label>
      <input {...props} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] focus:ring-2 focus:ring-[#2ec9c9]/20 transition" />
    </div>
  );
}

function ChangeEmailCard() {
  const { user, reload } = useAuth();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/change-email", { new_email: email, current_password: pw });
      toast({ title: "Email updated", description: `Your login email is now ${email}.` });
      setEmail(""); setPw("");
      await reload();
    } catch (err) {
      toast({ title: "Could not update email", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    } finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)] max-w-[480px]" data-testid="change-email-card">
      <div className="flex items-center gap-2 mb-1"><Mail className="text-[#2ec9c9]" size={18} /><h3 className="font-semibold text-[#16243b]">Login email</h3></div>
      <p className="text-sm text-[#8a97a8] mb-4">Current email: <strong className="text-[#16243b]" data-testid="current-email">{user?.email}</strong></p>
      <form onSubmit={submit} className="space-y-3">
        <Field label="New email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@business.com" data-testid="new-email-input" />
        <Field label="Current password" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} placeholder="••••••••" data-testid="email-current-password" />
        <button disabled={busy} data-testid="save-email-btn" className="gp-btn-teal rounded-xl px-6 py-2.5 font-medium disabled:opacity-60">{busy ? "Saving…" : "Update email"}</button>
      </form>
    </div>
  );
}

function ChangePasswordCard() {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [cf, setCf] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (nw.length < 8) { toast({ title: "Too short", description: "New password must be at least 8 characters", variant: "destructive" }); return; }
    if (nw !== cf) { toast({ title: "Passwords don't match", variant: "destructive" }); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast({ title: "Password changed", description: "Your password has been updated." });
      setCur(""); setNw(""); setCf("");
    } catch (err) {
      toast({ title: "Could not change password", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    } finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)] max-w-[480px]" data-testid="change-password-card">
      <div className="flex items-center gap-2 mb-4"><KeyRound className="text-[#2ec9c9]" size={18} /><h3 className="font-semibold text-[#16243b]">Password</h3></div>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Current password" type="password" required value={cur} onChange={(e) => setCur(e.target.value)} placeholder="••••••••" data-testid="cp-current" />
        <Field label="New password" type="password" required value={nw} onChange={(e) => setNw(e.target.value)} placeholder="At least 8 characters" data-testid="cp-new" />
        <Field label="Confirm new password" type="password" required value={cf} onChange={(e) => setCf(e.target.value)} placeholder="Re-enter password" data-testid="cp-confirm" />
        <button disabled={busy} data-testid="cp-submit" className="gp-btn-teal rounded-xl px-6 py-2.5 font-medium disabled:opacity-60">{busy ? "Saving…" : "Update password"}</button>
      </form>
    </div>
  );
}

export default function AccountTab() {
  return (
    <div className="grid gap-6 lg:grid-cols-2 max-w-[1000px]" data-testid="account-tab">
      <ChangeEmailCard />
      <ChangePasswordCard />
    </div>
  );
}
